import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useUserStore } from '../stores/useUser.store';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '@/customHooks/useAppContext';
import { Button } from '@/components/shadcn-ui/button';
import {
  FileText,
  HelpCircle,
  LogOut,
  Menu,
  Star,
  User,
  X,
  Home,
  Building2,
  ClipboardCheck,
  Info,
  Mail,
  LifeBuoy,
  CircleHelp,
  BookOpen,
  LogIn,
} from 'lucide-react';
import { appAxios } from '@/network/base/appAxios';
import { toast } from 'sonner';

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, auth } = useUserStore();
  const { t, i18n } = useTranslation();
  const { appImages } = useAppContext();
  const [open, setOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);

  useEffect(() => {
    if (open) {
      const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollBarWidth}px`;
    } else {
      document.body.style.overflow = 'auto';
      document.body.style.paddingRight = '0px';
    }
    return () => {
      document.body.style.overflow = 'auto';
      document.body.style.paddingRight = '0px';
    };
  }, [open]);

  const drawerRef = useRef(null);
  const profileMenuRef = useRef(null);
  const profileButtonRef = useRef(null);

  const handleLogout = () => {
    logout();
    navigate('/auth');
  };

  const handleLogin = async () => {
    try {
      const response = await appAxios.post('/Auth/v1.0/authurl', {
        type: 'login',
        grantType: 'email-password',
      });
      if (response.data.data.success && response.data.data.data?.authUrl) {
        window.location.href = response.data.data.data?.authUrl;
      } else {
        toast.error('Auth Url not received, please try again');
      }
    } catch {
      toast.error('Something went wrong, please try again');
    }
  };

  const handleSignUp = async () => {
    try {
      const response = await appAxios.post('/Auth/v1.0/authurl', {
        type: 'signup',
        grantType: 'email-password',
      });
      if (response.data.data.success && response.data.data.data?.authUrl) {
        window.location.href = response.data.data.data?.authUrl;
      } else {
        toast.error('Auth Url not received, please try again');
      }
    } catch {
      toast.error('Something went wrong, please try again');
    }
  };

  useEffect(() => {
    const handleOutsideClick = e => {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(e.target) &&
        profileButtonRef.current &&
        !profileButtonRef.current.contains(e.target)
      ) {
        setProfileMenuOpen(false);
      }
    };
    if (profileMenuOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [profileMenuOpen]);

  const primaryNavItems = [
    { label: 'Home', icon: <Home size={20} strokeWidth={1.5} />, to: '/', external: false },
    {
      label: 'Find a Pro',
      icon: <Star size={20} strokeWidth={1.5} className="text-yellow-400 fill-yellow-400" />,
      to: 'https://zermit.ai/contractors',
      external: true,
      highlight: true,
    },
    {
      label: 'Business',
      icon: <Building2 size={20} strokeWidth={1.5} />,
      to: 'https://zermit.ai/businesses',
      external: true,
    },
    {
      label: 'Subscription',
      icon: <ClipboardCheck size={20} strokeWidth={1.5} />,
      to: auth ? '/citypermit/subscription' : '/subscription',
      external: false,
    },
  ];

  const secondaryNavItems = [
    { label: 'About', icon: <Info size={20} strokeWidth={1.5} />, to: 'https://zermit.ai', external: true },
    { label: 'Contact', icon: <Mail size={20} strokeWidth={1.5} />, to: 'https://zermit.ai/contact', external: false },
    {
      label: 'Support',
      icon: <LifeBuoy size={20} strokeWidth={1.5} />,
      to: 'https://zermit.ai/support',
      external: false,
    },
    { label: 'FAQs', icon: <CircleHelp size={20} strokeWidth={1.5} />, to: 'https://zermit.ai/faq', external: false },
    { label: 'Blog', icon: <BookOpen size={20} strokeWidth={1.5} />, to: 'https://zermit.ai/blog', external: false },
  ];

  const renderDrawerLink = (item, onClick) => {
    const sharedClass = `flex items-center gap-4 px-5 py-3.5 transition-colors w-full text-left ${
      item.highlight ? 'text-yellow-400' : 'text-white'
    } hover:bg-white/5 rounded-xl`;

    if (item.external) {
      return (
        <a
          key={item.label}
          href={item.to}
          target="_blank"
          rel="noopener noreferrer"
          className={sharedClass}
          onClick={onClick}
        >
          {item.icon}
          <span className="text-[17px] font-medium">{item.label}</span>
        </a>
      );
    }
    return (
      <Link key={item.label} to={item.to} className={sharedClass} onClick={onClick}>
        {item.icon}
        <span className="text-[17px] font-medium">{item.label}</span>
      </Link>
    );
  };

  return (
    <>
      <nav className="dark shadow-sm sticky top-0 z-50 font-baloo-2-thin bg-black">
        <div className="md:hidden">
          <div className="flex items-center justify-center h-[44px] px-4 border-b border-white/10">
            <Link to={auth ? '/citypermit/project/new-chat' : 'https://zermit.ai'} replace={!auth}>
              <span className="text-xl text-white font-montserrat font-bold tracking-widest uppercase">Zermit</span>
            </Link>
          </div>

          <div className="flex items-center justify-center h-[40px] px-3">
            <div className="flex items-center gap-6">
              <Link
                to="https://zermit.ai/contractors"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-white text-sm font-medium underline underline-offset-2"
              >
                <img src="/ProIcon.png" alt="" className="size-4" />
                Find a Pro
              </Link>
              <Link
                to="https://zermit.ai/businesses"
                className="text-white text-sm font-medium underline underline-offset-2"
              >
                Business
              </Link>
              <Link
                to={auth ? '/citypermit/subscription' : '/subscription'}
                className="text-white text-sm font-medium underline underline-offset-2"
              >
                Subscription
              </Link>
              <button className="p-1.5 text-white gap-6" onClick={() => setOpen(true)} aria-label="Open menu">
                <Menu size={22} strokeWidth={1.8} />
              </button>
            </div>
          </div>
        </div>

        <div className="hidden md:flex items-center justify-between h-[60px] px-4 mx-auto">
          <Link
            to={auth ? '/citypermit/project/new-chat' : 'https://zermit.ai'}
            replace={!auth}
            className="flex-shrink-0"
          >
            <span className="text-2xl text-white font-montserrat font-bold tracking-widest uppercase px-2">Zermit</span>
          </Link>

          <div className="flex items-center gap-6">
            <Link
              to="https://zermit.ai/contractors"
              replace
              target="_blank"
              className="flex items-center text-white transition-colors text-lg font-medium underline underline-offset-2 gap-1.5"
            >
              <img src="/ProIcon.png" alt="Find a Pro" className="size-6" />
              Find a Pro
            </Link>

            <Link
              to="https://zermit.ai/businesses"
              className="flex items-center gap-1 text-white transition-colors text-lg font-medium underline underline-offset-2"
            >
              Business
            </Link>

            <Link
              to={auth ? '/citypermit/subscription' : '/subscription'}
              className="flex items-center gap-1 text-white transition-colors text-lg font-medium underline underline-offset-2"
            >
              Subscription
            </Link>

            {auth && (
              <Link
                to="https://zermit.ai"
                replace
                target="_blank"
                className="flex items-center gap-1 text-white transition-colors text-lg font-medium underline underline-offset-2"
              >
                About us
              </Link>
            )}

            {auth ? (
              <button
                ref={profileButtonRef}
                onClick={() => setProfileMenuOpen(prev => !prev)}
                className="w-9 h-9 rounded-full border border-[#515151] text-white flex items-center justify-center hover:bg-zinc-700 transition-colors"
                aria-label="Profile menu"
              >
                <User size={18} strokeWidth={1.5} />
              </button>
            ) : (
              <>
                <Button
                  onClick={handleLogin}
                  className="rounded-[20px] bg-black text-white border border-[#515151] px-6 py-2 hover:bg-black/80"
                >
                  Log in
                </Button>
                <Button
                  onClick={handleSignUp}
                  className="dark bg-secondary rounded-[20px] text-white border border-white/20 px-6 py-2 hover:bg-[#6A6A6A]"
                >
                  Sign Up
                </Button>
              </>
            )}
          </div>
        </div>
      </nav>

      {profileMenuOpen && auth && (
        <div
          ref={profileMenuRef}
          className="fixed right-4 z-40 w-52 bg-black rounded-2xl rounded-tr-none border border-white/10 overflow-hidden"
          style={{ top: 'calc(60px + 0.5rem)' }}
        >
          <Link
            to={auth ? '/citypermit/subscription' : '/subscription'}
            className="flex items-center gap-3 px-4 py-3.5 text-white hover:bg-white/10 transition-colors"
            onClick={() => setProfileMenuOpen(false)}
          >
            <Star size={13} className="text-pink-400 fill-pink-500" />
            <span className="text-lg font-medium">Pro Upgrade</span>
          </Link>
          <div className="h-px bg-white/10" />
          <button
            type="button"
            className="flex items-center gap-3 px-4 py-3.5 text-white hover:bg-white/10 transition-colors w-full text-left"
            onClick={() => {
              setProfileMenuOpen(false);
              handleLogout();
            }}
          >
            <LogOut size={16} strokeWidth={1.5} className="text-white/80" />
            <span className="text-lg font-medium">Log Out</span>
          </button>
        </div>
      )}

      {open && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-40"
          onClick={() => setOpen(false)}
          aria-hidden="true"
        />
      )}

      <div
        ref={drawerRef}
        className={`fixed top-0 right-0 h-full w-[78vw] max-w-[320px] bg-[#1a1a1a] z-50 flex flex-col transform transition-transform duration-300 ease-in-out ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
        aria-modal="true"
        role="dialog"
        aria-label="Navigation menu"
      >
        <div className="flex items-center justify-between px-5 py-6 border-b border-white/10">
          <span className="text-white font-montserrat font-bold tracking-widest uppercase text-xl">Zermit</span>
          <button
            onClick={() => setOpen(false)}
            className="w-8 h-8 flex items-center justify-center rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-colors"
            aria-label="Close menu"
          >
            <X size={22} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto py-3 px-2">
          <div className="flex flex-col gap-0.5">
            {primaryNavItems.map(item => renderDrawerLink(item, () => setOpen(false)))}
          </div>

          <div className="h-px bg-white/10 mx-3 my-3" />

          <div className="flex flex-col gap-0.5">
            {secondaryNavItems.map(item => renderDrawerLink(item, () => setOpen(false)))}
          </div>
        </div>

        <div className="px-4 pb-8 pt-4 border-t border-white/10">
          {auth ? (
            <div className="flex flex-col gap-2">
              <Link
                to={auth ? '/citypermit/subscription' : '/subscription'}
                className="flex items-center justify-center gap-2 w-full rounded-xl bg-white/5 border border-white/10 text-white py-3 text-[17px] font-medium hover:bg-white/10 transition-colors"
                onClick={() => setOpen(false)}
              >
                <Star size={15} className="text-pink-400 fill-pink-500" />
                Pro Upgrade
              </Link>
              <button
                type="button"
                className="flex items-center justify-center gap-2 w-full rounded-xl bg-white/5 border border-white/10 text-white py-3 text-[17px] font-medium hover:bg-white/10 transition-colors"
                onClick={() => {
                  setOpen(false);
                  handleLogout();
                }}
              >
                <LogOut size={16} strokeWidth={1.5} />
                Log Out
              </button>
            </div>
          ) : (
            <>
              <div className="flex flex-col gap-2">
                <Button
                  onClick={() => {
                    setOpen(false);
                    handleLogin();
                  }}
                  className="rounded-[20px] bg-black text-white border border-[#515151] px-6 py-2 hover:bg-black/80"
                >
                  Log in
                </Button>
                <Button
                  onClick={() => {
                    setOpen(false);
                    handleSignUp();
                  }}
                  className="dark bg-secondary rounded-[20px] text-white border border-white/20 px-6 py-2 hover:bg-[#6A6A6A]"
                >
                  Sign Up
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Navbar;
